/**
 * Staff elevation API (cdb_tqs#701, "PIM-lite") — tqs routes /api/v1/staff/*.
 *
 * A BIK admin self-elevates onto a foreign client for a time-boxed window
 * with a mandatory reason; the elevation IS its audit-log row. `/me` carries
 * the environment policy (enforced on prod only) and the caller's active
 * elevations for the Cortex badge; the log list is visible to ALL admins —
 * peer review is the control.
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

export interface StaffElevation {
  id: string;
  actor_user_id: string | null;
  actor_email: string;
  target_client_id: string | null;
  target_client_name: string;
  reason: string;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  is_active: boolean;
}

export interface StaffElevationStatus {
  enforced: boolean;
  default_ttl_hours: number;
  max_ttl_hours: number;
  active: StaffElevation[];
}

export interface StaffElevationCreatePayload {
  target_client_id: string;
  reason: string;
  ttl_hours?: number;
}

const BASE = `${CDB_TQS_API_BASE_URL}/staff`;

export const staffApi = {
  async getStaffElevationStatus(): Promise<StaffElevationStatus> {
    const response = await fetchWithLogging(`${BASE}/elevations/me`, { method: "GET" });
    return response.data as StaffElevationStatus;
  },

  async listStaffElevationLog(limit = 100, offset = 0): Promise<StaffElevation[]> {
    const response = await fetchWithLogging(
      `${BASE}/elevations?limit=${limit}&offset=${offset}`,
      { method: "GET" },
    );
    return response.data as StaffElevation[];
  },

  async createStaffElevation(data: StaffElevationCreatePayload): Promise<StaffElevation> {
    const response = await fetchWithLogging(`${BASE}/elevations`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.data as StaffElevation;
  },

  async endStaffElevation(elevationId: string): Promise<void> {
    await fetchWithLogging(`${BASE}/elevations/${elevationId}/end`, { method: "POST" });
  },
};
